import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { exportScheduleToExcel } from '../../../actions'
import { Spin, Input, Pagination, Radio, Button as AntButton, Tooltip, message } from 'antd'
import { COLORS } from 'theme'
import { SearchOutlined, ArrowUpOutlined, ArrowDownOutlined, UserAddOutlined, EditOutlined, DownloadOutlined } from '@ant-design/icons'
import Button from 'components/Button'
import DownloadButton from 'components/DownloadButton'
import CheckIconWhite from 'components/icons/CheckIconWhite'
import FilterIcon from 'components/icons/FilterIcon'
import ArrowDropDown from 'components/icons/ArrowDropDown'
import AssessmentProgressBar from 'components/AssessmentProgressBar'
import CompletedAssessmentProgressBar from 'components/CompletedAssessmentProgressBar'
import RightSideArrowIco from 'components/icons/RightSideArrowIco'
import DownSideArrowIco from 'components/icons/DownSideArrowIco'
import FilterButtonComponent from 'components/FilterButton'
import { FilterContainer, FilterPopup } from 'components/FilterButton/styles'
import ExpandableContent from './ExpandableContent'
import EditScheduleDrawer from './EditScheduleDrawer'
import EditAssessmentDrawer from './EditAssessmentDrawer'
import { resolveAssessmentEditTarget } from './editAssessmentTarget'

import {
  SearchSection,
  ShowingText,
  Overlay,
  TableTop,
  CustomStyledTable,
  AssessmentName,
  MenuFlex,
  StyledTab,
  SubmitFlex,
  StateCityMessage,
} from '../../../Style/style'

const formatDate = dateString => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

const assessmentTypeOptions = [
  { value: 'communication', label: 'Communication Assessment' },
  { value: 'behavior', label: 'Behavioral Assessment' },
  { value: 'aptitude', label: 'Aptitude' },
  { value: 'role_based', label: 'Role Based Assessment' },
  { value: 'technical_mcq', label: 'Technical MCQ' },
  { value: 'technical_coding', label: 'Technical Coding' },
  { value: '', label: 'All Assessment Types' },
]

const UnifiedAssessmentTable = ({
  activeAssessments = [],
  completedAssessments = [],
  loading = false,
  filters = {},
  activeMetrics,
  completedMetrics,
  activePage = 1,
  completedPage = 1,
  activeLimit = 10,
  completedLimit = 10,
  activeTotalItems = 0,
  completedTotalItems = 0,
  onFilterChange = () => {},
  onPageChange = () => {},
  stateCityPairs = [],
  onAssessmentClick = () => {},
  onAddCandidate = () => {},
  onScheduleUpdated = () => {},
  instituteId = null,
  passingYear = null,
}) => {
  const dispatch = useDispatch()
  const [trialPeriod, setTrialPeriod] = useState(filters.isTrial || false)
  const [subscription, setSubscription] = useState(
    filters.isSubscribed || false
  )
  const [showFilters, setShowFilters] = useState(false)
  const [selectedAssessmentType, setSelectedAssessmentType] = useState(
    filters.type_name || ''
  )
  const [searchValue, setSearchValue] = useState(filters.searchBy || '')
  const [statusFilter, setStatusFilter] = useState('active') // active, completed

  const { states, citiesByState } = useMemo(() => {
    const statesSet = new Set()
    const cityMap = new Map()

    stateCityPairs.forEach(([state, city]) => {
      statesSet.add(state)
      if (!cityMap.has(state)) {
        cityMap.set(state, new Set())
      }
      cityMap.get(state).add(city)
    })

    const statesList = Array.from(statesSet).map(state => ({
      state,
      stateId: state,
    }))

    const citiesMap = {}
    cityMap.forEach((cities, state) => {
      citiesMap[state] = Array.from(cities).map(city => ({
        city,
        cityId: city,
      }))
    })

    return {
      states: statesList,
      citiesByState: citiesMap,
    }
  }, [stateCityPairs])

  const [stateId, setStateId] = useState([])
  const [cityId, setCityId] = useState([])
  const [stateList, setStateList] = useState(states)
  const [cityList, setCityList] = useState([])
  const [stateNames, setStateNames] = useState(filters.states || [])
  const [cityName, setCityName] = useState(filters.cities || [])
  const [degreeSearch, setDegreeSearch] = useState('')
  const [check, setCheck] = useState(false)
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [editDrawerVisible, setEditDrawerVisible] = useState(false)
  const [editScheduleId, setEditScheduleId] = useState(null)
  const [editAssessmentTarget, setEditAssessmentTarget] = useState(null) // { mapId, mapType } for non-scheduled edit

  // Combine and process assessment data
  const combinedAssessments = useMemo(() => {
    const activeWithStatus = activeAssessments.map(assessment => ({
      ...assessment,
      status: 'active',
      statusAPI: assessment.status,
      statusIcon: <ArrowDownOutlined style={{ color: '#1890ff' }} />,
      statusText: 'Ongoing',
    }))

    const completedWithStatus = completedAssessments.map(assessment => ({
      ...assessment,
      status: 'completed',
      statusAPI: assessment.status,
      statusIcon: <ArrowUpOutlined style={{ color: '#52c41a' }} />,
      statusText: 'Completed',
    }))

    let combined = [...activeWithStatus, ...completedWithStatus]

    // Apply status filter
    if (statusFilter === 'active') {
      combined = activeWithStatus
    } else if (statusFilter === 'completed') {
      combined = completedWithStatus
    }

    return combined
  }, [activeAssessments, completedAssessments, statusFilter])

  const totalItems = activeTotalItems

  const handleFilterChange = useCallback(
    (key, value) => {
      const newFilters = {
        ...filters,
        ...(typeof key === 'object' ? key : { [key]: value }),
        updateFilters: true,
      }
      onFilterChange(newFilters)
    },
    [filters, onFilterChange]
  )

  useEffect(() => {
    handleFilterChange('type_name', filters.type_name || '')
  }, [])

  useEffect(() => {
    if (!filters.fromSearch && !filters.fromCheckbox) {
      if (filters.type_name !== undefined) {
        setSelectedAssessmentType(filters.type_name)
      }
      if (filters.isTrial !== undefined) {
        setTrialPeriod(filters.isTrial)
      }
      if (filters.isSubscribed !== undefined) {
        setSubscription(filters.isSubscribed)
      }
      if (filters.searchBy !== undefined && !filters.updateFilters) {
        setSearchValue(filters.searchBy || '')
      }
      setStateNames(filters.states || [])
      setCityName(filters.cities || [])
    }
  }, [filters])

  useEffect(() => {
    setStateList(states)
  }, [states])

  useEffect(() => {
    if (stateId?.length) {
      const availableCities = stateId.flatMap(
        state => citiesByState[state] || []
      )
      setCityList(availableCities)
    } else {
      setCityList([])
    }
  }, [stateId, citiesByState])

  useEffect(() => {
    setCityId([])
    setCityName([])
  }, [stateId])

  const handleSearch = useCallback(
    e => {
      const value = e.target.value
      setSearchValue(value)

      handleFilterChange({
        searchBy: value,
        fromSearch: true,
        updateFilters: true,
      })
    },
    [handleFilterChange]
  )

  const handleAssessmentTypeChange = value => {
    setSelectedAssessmentType(value)
  }

  const submitFilter = useCallback(() => {
    setShowFilters(false)

    const newFilters = {
      ...filters,
      type_name: selectedAssessmentType,
      states: stateNames || [],
      cities: cityName || [],
      pageNo: 0,
      updateFilters: true,
    }
    onFilterChange(newFilters)
  }, [filters, selectedAssessmentType, stateNames, cityName, onFilterChange])

  const handleTableChange = useCallback(
    (pagination, tableFilters, sorter) => {
      if (sorter) {
        handleFilterChange({
          sort: sorter.order === 'descend' ? 'desc' : 'asc',
          order: sorter.field,
        })
      }
    },
    [handleFilterChange]
  )

  const handlePageChange = useCallback(
    newPage => {
      onPageChange(newPage, {
        ...filters,
        type_name: selectedAssessmentType,
        pageOnly: true,
      })
    },
    [filters, onPageChange, selectedAssessmentType]
  )

  const getRowKey = record =>
    record.scheduleId || record.assessmentCorporateMapId || record.id

  const handleExpand = (expanded, record) => {
    const key = getRowKey(record)
    if (expanded) {
      setExpandedRowKeys([key])
    } else {
      setExpandedRowKeys(expandedRowKeys.filter(k => k !== key))
    }
  }

  const expandedRowRender = record => {
    return (
      <ExpandableContent
        record={record}
        onAssessmentClick={onAssessmentClick}
        onEditAssessment={mapId =>
          setEditAssessmentTarget({ mapId, mapType: 'institute' })
        }
      />
    )
  }

  // Check if a record has assessmentDetails to determine if it should be expandable
  const rowExpandable = record => {
    if (record.isOneTime) return false
    if (record.isStandalone) return false
    if (record.assessmentCorporateMapId) return false
    return record.assessmentDetails && record.assessmentDetails.length > 0
  }

  const columns = useMemo(
    () => [
      {
        title: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
            }}
          >
            <span>STATUS</span>
          </div>
        ),
        dataIndex: 'status',
        key: 'status',
        width: '120px',
        fixed: 'left',
        render: (status, record) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{record.statusAPI}</span>
          </div>
        ),
      },
      {
        title: 'ASSESSMENT TYPE / ASSMT. NAME',
        dataIndex: 'assessmentType',
        key: 'assessmentType',
        width: '230px',
        fixed: 'left',
        render: (text, record) => {
          const typeMap = {
            communication: 'Communication Assessment',
            behavior: 'Behavioral Assessment',
            aptitude: 'Aptitude',
            role_based: 'Role Based Assessment',
            technical_mcq: 'Technical MCQ',
            technical_coding: 'Technical Coding',
          }
          const normalizedType = (text || '').toLowerCase().replace(/[\s-]+/g, '_')
          const isBroadcast = record.isBroadcast || record.mode === 'Broadcast'

          // Helper to get clean schedule name
          const getCleanScheduleName = rec => {
            const cName = rec.candidateListName
            if (cName && cName !== 'N/A') {
              return cName.replace(/\s*-\s*Student List$/i, '').trim()
            }
            return rec.scheduleName || rec.name
          }

          return (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>{typeMap[normalizedType] || typeMap[text] || text || '-'}</span>
                {isBroadcast && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: '1px 6px',
                    background: '#EAEDFB', color: '#3249D7', borderRadius: 4,
                  }}>BROADCAST</span>
                )}
              </div>
              <AssessmentName
                onClick={e => {
                  const latestMapId =
                    record.latestAssessmentInstituteMapId ||
                    record.latestAssessmentCorporateMapId
                  const isCorporate = !!record.assessmentCorporateMapId

                  // Block click if Upcoming and has a map ID
                  if (record.status === 'Upcoming' && latestMapId) return

                  e.stopPropagation()

                  // Corporate assessments: pass directly with corporate map ID
                  if (isCorporate) {
                    onAssessmentClick({
                      ...record,
                      id: record.assessmentCorporateMapId,
                      scheduleName: record.name || getCleanScheduleName(record),
                    })
                    return
                  }

                  // Institute: no map ID means diagnosis
                  if (!latestMapId) {
                    const diagnosisData = {
                      ...record,
                      scheduleId: record.scheduleId,
                      isDiagnosis: true,
                      assessmentType: record.assessmentType,
                      scheduleName: getCleanScheduleName(record),
                    }
                    onAssessmentClick(diagnosisData)
                    return
                  }

                  const detail = record.assessmentDetails?.find(
                    d =>
                      d.assessmentInstituteMapId ===
                      record.latestAssessmentInstituteMapId
                  )

                  let scheduleNum = 1
                  if (detail && record.assessmentDetails) {
                    const idx = record.assessmentDetails.findIndex(
                      d =>
                        d.assessmentInstituteMapId ===
                        detail.assessmentInstituteMapId
                    )
                    if (idx !== -1) {
                      scheduleNum = idx + 1
                    }
                  }

                  const assessmentToPass = detail
                    ? {
                        ...record,
                        ...detail,
                        id: detail.assessmentInstituteMapId,
                        assessmentInstituteMapId:
                          detail.assessmentInstituteMapId,
                        scheduleName: getCleanScheduleName(record),
                        scheduleNumber: scheduleNum,
                      }
                    : {
                        ...record,
                        scheduleName: getCleanScheduleName(record),
                        scheduleNumber: 1,
                      }

                  onAssessmentClick(assessmentToPass)
                }}
                role="button"
                tabIndex={0}
                style={{
                  cursor:
                    record.assessmentCorporateMapId ||
                    record.status !== 'Upcoming' ||
                    !record.latestAssessmentInstituteMapId
                      ? 'pointer'
                      : 'not-allowed',
                  opacity:
                    record.assessmentCorporateMapId ||
                    record.status !== 'Upcoming' ||
                    !record.latestAssessmentInstituteMapId
                      ? 1
                      : 0.5,
                }}
                onKeyPress={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.target.click()
                  }
                }}
              >
                {record.scheduleName || record.name}
              </AssessmentName>
            </div>
          )
        },
      },
      {
        title: 'ASSMT. SENT / TAKEN',
        dataIndex: 'assessmentSent',
        key: 'assessmentProgress',
        width: 280,
        render: (sent, record) => {
          const completed = record.assessmentsTaken || 0
          const total = sent || record.assessmentsSent || 0

          if (record.status === 'completed') {
            return (
              <CompletedAssessmentProgressBar
                sent={total}
                completed={completed}
              />
            )
          } else {
            return (
              <AssessmentProgressBar sent={total} completed={completed} />
            )
          }
        },
      },
      {
        title: 'Expired Assmts.',
        dataIndex: 'candidates',
        key: 'expiredAssessments',
        width: '120px',
        render: (candidates, record) => {
          const candidateCount = record.expiredAssessments || 0
          return (
            <div style={{ fontWeight: '500', color: '#000000' }}>
              {candidateCount}
            </div>
          )
        },
      },
      {
        title: 'CANDIDATES',
        dataIndex: 'candidates',
        key: 'candidates',
        width: '140px',
        render: (candidates, record) => {
          const candidateCount = record.totalCandidates || 0
          return (
            <div style={{ fontWeight: '500', color: '#000000' }}>
              {candidateCount}
            </div>
          )
        },
      },
      {
        title: 'Assmt. Validity',
        dataIndex: 'candidates',
        key: 'assessmentValidity',
        width: '140px',
        render: (candidates, record) => {
          return <div>{record.assessmentValidity}</div>
        },
      },
      {
        title: 'Schedule Start Date & End Date',
        dataIndex: 'assessmentSubDate',
        key: 'assessmentSubDate',
        width: '200px',
        sorter: true,
        render: (startDate, record) => {
          if (record.status === 'completed') {
            return <div>{formatDate(record.completedDate)}</div>
          } else {
            return (
              <div>
                {formatDate(record.startDate)}{' '}
                {record.endDate ? ` - ${formatDate(record.endDate)}` : ''}
              </div>
            )
          }
        },
      },
      {
        title: 'ACTIONS',
        key: 'actions',
        width: '120px',
        align: 'center',
        render: (_, record) => (
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
            <Tooltip title="Add Candidate">
              <AntButton
                type="text"
                size="small"
                icon={<UserAddOutlined />}
                style={{ color: '#3249d7', borderRadius: 6, width: 32, height: 32 }}
                onClick={(e) => {
                  e.stopPropagation()
                  if (record.isStandalone || record.isOneTime) {
                    onAddCandidate({
                      ...record,
                      assessmentInstituteMapId: record.assessmentInstituteMapId || record.scheduleId,
                      isOneTime: true,
                      isStandalone: record.isStandalone,
                    })
                  } else {
                    const latestDetail = record.assessmentDetails?.find(
                      d => d.assessmentInstituteMapId === record.latestAssessmentInstituteMapId
                    ) || record.assessmentDetails?.[record.assessmentDetails.length - 1]
                    onAddCandidate({
                      ...record,
                      assessmentInstituteMapId: latestDetail?.assessmentInstituteMapId,
                      isOneTime: latestDetail?.isOneTime || false,
                      scheduleId: record.scheduleId,
                    })
                  }
                }}
              />
            </Tooltip>
            <Tooltip title="Edit Assessment">
              <AntButton
                type="text"
                size="small"
                icon={<EditOutlined />}
                style={{ color: '#595959', borderRadius: 6, width: 32, height: 32 }}
                onClick={(e) => {
                  e.stopPropagation()
                  const target = resolveAssessmentEditTarget(record)
                  if (target.kind === 'schedule') {
                    setEditScheduleId(target.scheduleId)
                    setEditDrawerVisible(true)
                  } else if (target.kind === 'assessment') {
                    setEditAssessmentTarget({ mapId: target.mapId, mapType: target.mapType })
                  }
                }}
              />
            </Tooltip>
            {record.scheduleId && !record.isOneTime && !record.isStandalone && (
              <Tooltip title="Export">
                <DownloadButton
                  type="text"
                  size="small"
                  icon={<DownloadOutlined />}
                  style={{ color: '#595959', borderRadius: 6, width: 32, height: 32 }}
                  onClick={async (e) => {
                    e.stopPropagation()
                    try {
                      message.loading({ content: 'Exporting schedule...', key: 'scheduleExport' })
                      await dispatch(exportScheduleToExcel(record.scheduleId, instituteId, passingYear))
                      message.success({ content: 'Schedule exported!', key: 'scheduleExport' })
                    } catch (error) {
                      message.error({ content: 'Export failed', key: 'scheduleExport' })
                    }
                  }}
                />
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [onAssessmentClick, onAddCandidate, statusFilter, setStatusFilter, dispatch, instituteId, passingYear]
  )

  // Calculate dynamic values based on status filter
  const currentTotalItems =
    statusFilter === 'active' ? activeTotalItems : completedTotalItems
  const currentLimit =
    statusFilter === 'active' ? activeLimit : completedLimit
  const currentPage =
    statusFilter === 'active' ? activePage : completedPage

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      />
      <TableTop>
        <SearchSection>
          <Input
            placeholder="Search by keyword"
            prefix={<SearchOutlined style={{ color: COLORS.GRAY_LIGHT }} />}
            value={searchValue}
            onChange={handleSearch}
          />
        </SearchSection>

        <ShowingText>
          Showing {combinedAssessments.length} of{' '}
          {currentTotalItems}
        </ShowingText>
      </TableTop>

      <Spin spinning={loading}>
        <CustomStyledTable
          columns={columns}
          dataSource={combinedAssessments}
          pagination={false}
          rowKey={record =>
            record.scheduleId || record.assessmentCorporateMapId || record.id
          }
          scroll={{ x: '1280px' }}
          onChange={handleTableChange}
          expandable={{
            expandedRowRender,
            expandedRowKeys: expandedRowKeys,
            onExpand: handleExpand,
            rowExpandable: rowExpandable,
            expandIconColumnIndex: 0,
            expandIcon: ({ expanded, onExpand, record }) =>
              rowExpandable(record) ? (
                <span
                  onClick={e => {
                    e.stopPropagation()
                    onExpand(record, e)
                  }}
                  style={{
                    cursor: 'pointer',
                    marginRight: 8,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 16,
                    height: 16,
                  }}
                >
                  {expanded ? <DownSideArrowIco /> : <RightSideArrowIco />}
                </span>
              ) : null,
          }}
        />
      </Spin>

      {currentTotalItems > currentLimit && (
        <div
          style={{
            margin: '16px',
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Pagination
            current={currentPage}
            total={currentTotalItems}
            pageSize={currentLimit}
            onChange={handlePageChange}
          />
        </div>
      )}

      <EditScheduleDrawer
        visible={editDrawerVisible}
        onClose={() => {
          setEditDrawerVisible(false)
          setEditScheduleId(null)
        }}
        scheduleId={editScheduleId}
        onSuccess={() => {
          onScheduleUpdated()
        }}
      />
      <EditAssessmentDrawer
        visible={!!editAssessmentTarget}
        mapId={editAssessmentTarget?.mapId}
        mapType={editAssessmentTarget?.mapType}
        onClose={() => setEditAssessmentTarget(null)}
        onSuccess={() => {
          onScheduleUpdated()
          setEditAssessmentTarget(null)
        }}
      />
    </>
  )
}

export default UnifiedAssessmentTable
